import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings2, EyeOff, Eye, ClipboardCheck, AlertTriangle, Plus, X, Lock, Unlock, History, CheckCircle2, XCircle, GitMerge, UserCheck, Wand2, FolderPlus } from "lucide-react";
import { TaxonomyManager } from "@/components/TaxonomyManager";
import { toast } from "sonner";

const REVIEWERS = ["Dr. E. Vandermeer — MarksMan Scientific Review", "Dr. H. Okafor — MarksMan Scientific Review"];
const err = (e, msg) => toast.error(e.response?.data?.detail || msg);

function ProjectSetup() {
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [proj, setProj] = useState({ name: "", company: "", asset: "", indication: "", therapeutic_area: "", geographies: "" });
  const [comp, setComp] = useState({ name: "", company: "", mechanism: "", threat_level: "Medium" });
  const [question, setQuestion] = useState("");
  const [newType, setNewType] = useState("");
  const [geos, setGeos] = useState("");

  const load = () => {
    api.get("/projects/active").then((r) => { setProject(r.data); setGeos((r.data.geographies || []).join(", ") || r.data.scope); });
    api.get("/projects").then((r) => setProjects(r.data.projects));
    api.get("/competitors").then((r) => setCompetitors(r.data.competitors));
    api.get("/admin/source-types").then((r) => setSourceTypes(r.data.source_types));
  };
  useEffect(load, []);

  const createProject = () => {
    if (!proj.name || !proj.asset || !proj.indication) { toast.error("Name, asset and indication are required"); return; }
    api.post("/admin/projects", { ...proj, geographies: proj.geographies.split(",").map((g) => g.trim()).filter(Boolean) })
      .then((r) => { toast.success(`Project ${r.data.id} created (draft)`); setProj({ name: "", company: "", asset: "", indication: "", therapeutic_area: "", geographies: "" }); load(); })
      .catch((e) => err(e, "Project creation failed"));
  };

  const addCompetitor = () => {
    if (!comp.name) { toast.error("Competitor name required"); return; }
    api.post("/admin/competitors", comp)
      .then(() => { toast.success("Competitor added"); setComp({ name: "", company: "", mechanism: "", threat_level: "Medium" }); load(); })
      .catch((e) => err(e, "Failed to add competitor"));
  };

  const addQuestion = () => {
    if (!question) return;
    api.post("/admin/questions", { question })
      .then((r) => { toast.success(`Question ${r.data.id} defined`); setQuestion(""); })
      .catch((e) => err(e, "Failed to add question"));
  };

  const saveGeos = () => {
    api.patch("/admin/projects/active/geographies", { geographies: geos.split(",").map((g) => g.trim()).filter(Boolean) })
      .then((r) => { toast.success(`Geographies: ${r.data.scope}`); load(); })
      .catch((e) => err(e, "Failed to update geographies"));
  };

  const addType = () => {
    if (!newType) return;
    api.post("/admin/source-types", { name: newType })
      .then((r) => { setSourceTypes(r.data.source_types); setNewType(""); toast.success("Source type added"); })
      .catch((e) => err(e, "Failed"));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="admin-active-project">
        <h3 className="font-heading text-sm font-bold text-slate-900 flex items-center gap-2 mb-3"><Settings2 className="w-4 h-4 text-teal-600" />Active project</h3>
        {project && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
            {[["Asset / Brand", `${project.asset} (${project.brand})`], ["Company", project.company], ["Indication", project.indication], ["Scope", project.scope]].map(([l, v]) => (
              <div key={l} className={l === "Indication" ? "col-span-2" : ""}>
                <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{l}</p>
                <p className="text-xs text-slate-700 font-medium leading-snug">{v}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <Input value={geos} onChange={(e) => setGeos(e.target.value)} placeholder="Geographies (comma-separated)" className="h-8 text-xs max-w-md" data-testid="geo-config-input" />
          <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" onClick={saveGeos} data-testid="geo-config-save">Configure geographies</Button>
        </div>
        {projects.length > 1 && (
          <p className="text-[11px] text-slate-400 mt-2" data-testid="project-list">All projects: {projects.map((p) => `${p.id} ${p.name || p.asset} (${p.status})`).join(" · ")}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="admin-create-project">
          <h3 className="font-heading text-sm font-bold text-slate-900 flex items-center gap-2 mb-3"><FolderPlus className="w-4 h-4 text-teal-600" />Create project</h3>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Project name *" value={proj.name} onChange={(e) => setProj({ ...proj, name: e.target.value })} className="h-8 text-xs col-span-2" data-testid="project-name-input" />
            <Input placeholder="Asset *" value={proj.asset} onChange={(e) => setProj({ ...proj, asset: e.target.value })} className="h-8 text-xs" data-testid="project-asset-input" />
            <Input placeholder="Company" value={proj.company} onChange={(e) => setProj({ ...proj, company: e.target.value })} className="h-8 text-xs" />
            <Input placeholder="Indication *" value={proj.indication} onChange={(e) => setProj({ ...proj, indication: e.target.value })} className="h-8 text-xs col-span-2" data-testid="project-indication-input" />
            <Input placeholder="Therapeutic area" value={proj.therapeutic_area} onChange={(e) => setProj({ ...proj, therapeutic_area: e.target.value })} className="h-8 text-xs" />
            <Input placeholder="Geographies (comma-sep)" value={proj.geographies} onChange={(e) => setProj({ ...proj, geographies: e.target.value })} className="h-8 text-xs" />
          </div>
          <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white mt-2" onClick={createProject} data-testid="create-project-btn"><Plus className="w-3 h-3 mr-1" />Create project</Button>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="admin-add-competitor">
          <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Add competitor</h3>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name *" value={comp.name} onChange={(e) => setComp({ ...comp, name: e.target.value })} className="h-8 text-xs" data-testid="competitor-name-input" />
            <Input placeholder="Company" value={comp.company} onChange={(e) => setComp({ ...comp, company: e.target.value })} className="h-8 text-xs" />
            <Input placeholder="Mechanism" value={comp.mechanism} onChange={(e) => setComp({ ...comp, mechanism: e.target.value })} className="h-8 text-xs" />
            <Select value={comp.threat_level} onValueChange={(v) => setComp({ ...comp, threat_level: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="competitor-threat-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Critical", "High", "Medium", "Watch"].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white mt-2" onClick={addCompetitor} data-testid="add-competitor-btn"><Plus className="w-3 h-3 mr-1" />Add competitor</Button>
          <p className="text-[11px] text-slate-400 mt-2">{competitors.length} tracked: {competitors.map((c) => c.name).join(", ")}</p>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="admin-add-question">
          <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Define intelligence question</h3>
          <div className="flex gap-2">
            <Input placeholder="e.g., How will camizestrant launch timing affect 2L sequencing?" value={question} onChange={(e) => setQuestion(e.target.value)} className="h-8 text-xs" data-testid="question-input" />
            <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white shrink-0" onClick={addQuestion} data-testid="add-question-btn"><Plus className="w-3 h-3 mr-1" />Define</Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="admin-source-types">
          <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Manage source types</h3>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {sourceTypes.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[10px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">
                {t}
                <button onClick={() => api.delete(`/admin/source-types/${encodeURIComponent(t)}`).then((r) => { setSourceTypes(r.data.source_types); toast.success("Removed"); }).catch((e) => err(e, "Failed"))}
                  data-testid={`remove-source-type-${t.toLowerCase().replace(/\W+/g, "-")}`}>
                  <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input placeholder="New source type" value={newType} onChange={(e) => setNewType(e.target.value)} className="h-8 text-xs" data-testid="source-type-input" />
            <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white shrink-0" onClick={addType} data-testid="add-source-type-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Source materials are uploaded via Source Library and Evidence Upload & Analysis; extracted HCP statements are reviewed below in the Review Queue.</p>
        </div>
      </div>
    </div>
  );
}

function ReviewQueue() {
  const [insights, setInsights] = useState([]);
  const [themes, setThemes] = useState([]);
  const [mergeSel, setMergeSel] = useState([]);
  const [classifying, setClassifying] = useState(null);

  const load = () => {
    api.get("/admin/insights").then((r) => setInsights(r.data.insights));
    api.get("/themes").then((r) => setThemes(r.data.themes));
  };
  useEffect(load, []);

  const act = (id, action, value) => {
    api.patch(`/admin/insights/${id}`, { action, value })
      .then(() => { toast.success(`${action.replace("_", " ")} applied`); load(); })
      .catch((e) => err(e, "Action failed"));
  };

  const toggleMerge = (id) => setMergeSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2)));

  const doMerge = () => {
    api.post("/admin/insights/merge", { primary_id: mergeSel[0], duplicate_id: mergeSel[1] })
      .then(() => { toast.success(`${mergeSel[1]} merged into ${mergeSel[0]}`); setMergeSel([]); load(); })
      .catch((e) => err(e, "Merge failed"));
  };

  const toggleAnon = (i) => {
    api.post(`/admin/insights/${i.id}/anonymise`, { anonymised: !i.attribution?.anonymised_by_admin })
      .then(() => { toast.success("Attribution updated"); load(); })
      .catch((e) => err(e, "Failed"));
  };

  const STATUS_CLS = { "Reviewed & verified": "bg-emerald-50 text-emerald-700 border-emerald-200", "Under review": "bg-sky-50 text-sky-700 border-sky-200", Rejected: "bg-red-50 text-red-600 border-red-200" };

  return (
    <div className="space-y-3" data-testid="review-queue">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">{insights.length} statements · {insights.filter((i) => i.review_status === "Under review").length} under review · {insights.filter((i) => i.merged_into).length} merged</p>
        <p className="text-[10px] text-amber-700 mt-1">Statements under review remain visible here but cannot support approved findings until verified.</p>
        {mergeSel.length === 2 && (
          <Button className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={doMerge} data-testid="merge-btn">
            <GitMerge className="w-3 h-3 mr-1" />Merge {mergeSel[1]} into {mergeSel[0]}
          </Button>
        )}
      </div>
      {insights.map((i) => (
        <div key={i.id} className={`bg-white border rounded-xl p-4 shadow-sm ${i.merged_into ? "opacity-50 border-slate-100" : "border-slate-200/80"}`} data-testid={`review-row-${i.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <input type="checkbox" checked={mergeSel.includes(i.id)} onChange={() => toggleMerge(i.id)} disabled={!!i.merged_into}
                  className="accent-indigo-600" data-testid={`merge-check-${i.id}`} title="Select for merge" />
                <span className="font-mono text-[10px] text-slate-400">{i.id}</span>
                <Badge variant="outline" className={`text-[9px] ${STATUS_CLS[i.review_status] || "bg-slate-50 text-slate-500 border-slate-200"}`} data-testid={`review-status-${i.id}`}>{i.review_status || "—"}</Badge>
                <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{i.sentiment}</Badge>
                {i.merged_into && <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-600 border-indigo-200">Merged into {i.merged_into}</Badge>}
                {i.assigned_reviewer && <span className="text-[9px] text-teal-700 flex items-center gap-0.5"><UserCheck className="w-3 h-3" />{i.assigned_reviewer.split(" — ")[0]}</span>}
              </div>
              <p className="text-xs text-slate-700 leading-snug line-clamp-2">{i.statement_type === "AI-generated paraphrase" ? <><span className="font-mono text-[9px] uppercase tracking-wider text-rose-600 mr-1">AI paraphrase</span>{i.quote}</> : `"${i.quote}"`}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{i.hcp_role} · {i.country} · {i.source_type}</p>
            </div>
            {!i.merged_into && (
              <div className="flex flex-wrap gap-1 shrink-0 justify-end max-w-[280px]">
                <button onClick={() => act(i.id, "approve")} data-testid={`approve-${i.id}`}
                  className="flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3" />Approve</button>
                <button onClick={() => act(i.id, "reject")} data-testid={`reject-${i.id}`}
                  className="flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-0.5 hover:bg-red-100"><XCircle className="w-3 h-3" />Reject</button>
                <button onClick={() => setClassifying(classifying === i.id ? null : i.id)} data-testid={`classify-${i.id}`}
                  className="flex items-center gap-1 text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2 py-0.5 hover:bg-violet-100"><Wand2 className="w-3 h-3" />Reclassify</button>
                {i.attribution?.type === "named" && (
                  <button onClick={() => toggleAnon(i)} data-testid={`anon-${i.id}`}
                    className="flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5 hover:bg-slate-100">
                    {i.attribution.anonymised_by_admin ? <><Eye className="w-3 h-3" />Restore</> : <><EyeOff className="w-3 h-3" />Anonymise</>}
                  </button>
                )}
                <Select value={i.assigned_reviewer || ""} onValueChange={(v) => act(i.id, "assign_reviewer", { reviewer: v })}>
                  <SelectTrigger className="h-6 w-32 text-[10px]" data-testid={`assign-reviewer-${i.id}`}><SelectValue placeholder="Assign reviewer" /></SelectTrigger>
                  <SelectContent>{REVIEWERS.map((r) => <SelectItem key={r} value={r} className="text-xs">{r.split(" — ")[0]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {classifying === i.id && (
            <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-100" data-testid={`classify-panel-${i.id}`}>
              <span className="text-[10px] font-mono uppercase tracking-widest text-violet-600">Correct AI classification:</span>
              <Select onValueChange={(v) => act(i.id, "classify", { sentiment: v })}>
                <SelectTrigger className="h-6 w-28 text-[10px]" data-testid={`classify-sentiment-${i.id}`}><SelectValue placeholder="Sentiment" /></SelectTrigger>
                <SelectContent>{["positive", "mixed", "neutral", "negative"].map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={(v) => act(i.id, "classify", { theme_id: v })}>
                <SelectTrigger className="h-6 w-48 text-[10px]"><SelectValue placeholder="Theme" /></SelectTrigger>
                <SelectContent>{themes.map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={(v) => act(i.id, "classify", { confidence_tier: v })}>
                <SelectTrigger className="h-6 w-28 text-[10px]"><SelectValue placeholder="Confidence" /></SelectTrigger>
                <SelectContent>{["High", "Moderate", "Emerging"].map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AuditLock() {
  const [audit, setAudit] = useState([]);
  const [project, setProject] = useState(null);

  const load = () => {
    api.get("/admin/audit").then((r) => setAudit(r.data.audit));
    api.get("/projects/active").then((r) => setProject(r.data));
  };
  useEffect(load, []);

  const toggleLock = () => {
    api.post("/admin/lock", { locked: !project.locked })
      .then((r) => { toast.success(r.data.locked ? "Final report locked — dataset frozen" : "Report unlocked"); load(); })
      .catch((e) => err(e, "Failed"));
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-5 border flex items-start justify-between gap-4 ${project?.locked ? "bg-red-50 border-red-200" : "bg-white border-slate-200/80 shadow-sm"}`} data-testid="lock-panel">
        <div className="flex items-start gap-3">
          {project?.locked ? <Lock className="w-5 h-5 text-red-600 shrink-0 mt-0.5" /> : <Unlock className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />}
          <div>
            <h3 className="font-heading text-sm font-bold text-slate-900">Final cross-sectional report {project?.locked ? "is LOCKED" : "is open"}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {project?.locked
                ? `Locked ${project.locked_at?.slice(0, 10)} — insight reviews, classifications, merges, competitor and question changes are blocked until unlocked.`
                : "Locking freezes the dataset: review actions, reclassifications, merges and configuration changes will be blocked, marking the cross-sectional assessment as final."}
            </p>
          </div>
        </div>
        <Button onClick={toggleLock} data-testid="lock-toggle-btn"
          className={`h-8 text-xs shrink-0 ${project?.locked ? "bg-slate-700 hover:bg-slate-800" : "bg-red-600 hover:bg-red-700"} text-white`}>
          {project?.locked ? <><Unlock className="w-3.5 h-3.5 mr-1" />Unlock report</> : <><Lock className="w-3.5 h-3.5 mr-1" />Lock final report</>}
        </Button>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="audit-log">
        <h3 className="font-heading text-sm font-bold text-slate-900 flex items-center gap-2 mb-3"><History className="w-4 h-4 text-teal-600" />Audit history <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 font-normal">{audit.length} entries</span></h3>
        {audit.length === 0 && <p className="text-xs text-slate-400">No administrative actions recorded yet.</p>}
        <div className="space-y-1.5">
          {audit.map((a) => (
            <div key={a.id} className="flex items-start gap-3 text-xs border-b border-slate-50 pb-1.5" data-testid={`audit-entry-${a.id}`}>
              <span className="font-mono text-[10px] text-slate-400 shrink-0 w-36">{a.ts.slice(0, 16).replace("T", " ")}</span>
              <span className="font-semibold text-slate-700 shrink-0 w-44">{a.action}</span>
              <span className="font-mono text-[10px] text-teal-600 shrink-0 w-20">{a.target}</span>
              <span className="text-slate-500 min-w-0">{a.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Governance() {
  const [topics, setTopics] = useState([]);
  useEffect(() => { api.get("/consensus").then((r) => setTopics(r.data.topics)); }, []);
  const flagged = topics.filter((t) => t.consensus_score - t.evidence_strength >= 5);
  return (
    <div className="space-y-4">
      <TaxonomyManager />
      <div className="bg-white border border-slate-200/80 rounded-xl p-6 shadow-sm" data-testid="admin-review-calibration">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck className="w-4 h-4 text-teal-600" />
          <h3 className="font-heading text-base font-bold text-slate-900">MarksMan Scientific Review — Calibration Watch</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Topics where HCP consensus outruns evidence strength are flagged for reviewer scrutiny to prevent over-claiming.</p>
        {flagged.length === 0 ? <p className="text-xs text-slate-400">No topics currently flagged.</p> : (
          <div className="space-y-2">
            {flagged.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 bg-amber-50/50 border border-amber-100 rounded-lg px-4 py-2.5">
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{t.topic}</p>
                    <p className="text-[11px] text-slate-500">Consensus {t.consensus_score} vs evidence {t.evidence_strength} (Δ{t.consensus_score - t.evidence_strength})</p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] shrink-0">Review calibration</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminReview() {
  return (
    <div data-testid="admin-view">
      <Tabs defaultValue="setup">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="setup" className="text-xs" data-testid="tab-project-setup">Project Setup</TabsTrigger>
          <TabsTrigger value="review" className="text-xs" data-testid="tab-review-queue">Review Queue</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs" data-testid="tab-audit-lock">Audit & Lock</TabsTrigger>
          <TabsTrigger value="governance" className="text-xs" data-testid="tab-governance-cal">Taxonomy & Calibration</TabsTrigger>
        </TabsList>
        <TabsContent value="setup" className="mt-4"><ProjectSetup /></TabsContent>
        <TabsContent value="review" className="mt-4"><ReviewQueue /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLock /></TabsContent>
        <TabsContent value="governance" className="mt-4"><Governance /></TabsContent>
      </Tabs>
    </div>
  );
}
